/* Galaxy Frog behaviour, loaded on every page: moving background and pause motion, header menus,
   cart count, country selector, and the homepage product filters, video player and artwork carousel.
   Every part looks for data-gf-* attributes inside its own section, so sections can be
   added, removed and reordered in the theme editor. */
(() => {
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)');
  const observers = new WeakMap();
  const carouselTimers = new WeakMap();
  const carouselCleanups = new WeakMap();
  let paused = reduced.matches;

  /* ---------- Pause motion ---------- */
  function backgroundVideos() {
    return [...document.querySelectorAll('[data-gf-atmosphere] video, [data-gf-hero-video] video')];
  }

  function updateMotionButtons() {
    document.querySelectorAll('[data-gf-motion]').forEach((button) => {
      button.textContent = paused ? button.dataset.resumeLabel : button.dataset.pauseLabel;
      button.setAttribute('aria-pressed', String(paused));
      button.onclick = () => setPaused(!paused);
    });
  }

  function setPaused(value) {
    paused = value;
    document.body.classList.toggle('paused', paused);
    backgroundVideos().forEach((video) => {
      if (paused) {
        video.pause();
      } else {
        const playing = video.play();
        if (playing) playing.catch(() => {});
      }
    });
    updateMotionButtons();
  }

  /* ---------- Product grid ---------- */
  function initShop(shop) {
    if (shop.dataset.initialized) return;
    shop.dataset.initialized = 'true';

    const grid = shop.querySelector('[data-gf-grid]');
    const cards = [...grid.querySelectorAll('[data-gf-card]')];
    cards.forEach((card) => {
      const preview = card.querySelector('[data-gf-product-preview]');
      const image = preview && preview.querySelector('.gf-card-preview-image');
      const sources = preview ? [...preview.querySelectorAll('[data-gf-mockup-src]')] : [];
      const previous = preview && preview.querySelector('[data-gf-mockup-prev]');
      const next = preview && preview.querySelector('[data-gf-mockup-next]');
      const position = preview && preview.querySelector('[data-gf-mockup-position]');
      if (!image || sources.length < 2 || !previous || !next) return;
      const previews = sources.map((source) => ({
        src: source.dataset.gfMockupSrc,
        alt: source.dataset.gfMockupAlt || image.alt,
        current: source.hasAttribute('data-gf-mockup-current')
      }));
      let current = Math.max(0, previews.findIndex((item) => item.current));
      function show(index) {
        current = (index + previews.length) % previews.length;
        image.removeAttribute('srcset');
        image.src = previews[current].src;
        image.alt = previews[current].alt;
        if (position) position.textContent = `${current + 1} / ${previews.length}`;
      }
      show(current);
      previous.addEventListener('click', () => show(current - 1));
      next.addEventListener('click', () => show(current + 1));
    });
    const more = shop.querySelector('[data-gf-more]');
    const sort = shop.querySelector('[data-gf-sort]');
    const count = shop.querySelector('[data-gf-count]');
    const empty = shop.querySelector('[data-gf-empty]');
    const controls = shop.querySelector('[data-gf-controls]');
    const filterButtons = [...shop.querySelectorAll('[data-filter]')];
    const perPage = Number(shop.dataset.perPage) || 12;
    const ordered = [...cards];
    const initialCategory = filterButtons.find((button) => button.dataset.filter !== 'all');
    let category = initialCategory ? initialCategory.dataset.filter : 'all';
    let limit = perPage;

    function render() {
      const visible = ordered.filter((card) => category === 'all' || card.dataset.category === category);
      if (sort && sort.value !== 'featured') {
        const direction = sort.value === 'low' ? 1 : -1;
        visible.sort((a, b) => (Number(a.dataset.price) - Number(b.dataset.price)) * direction);
      }
      cards.forEach((card) => {
        card.hidden = true;
      });
      visible.forEach((card, index) => {
        grid.append(card);
        card.hidden = index >= limit;
      });
      if (count) {
        count.textContent = `${visible.length} ${visible.length === 1 ? shop.dataset.piece : shop.dataset.pieces}`;
      }
      if (more) more.hidden = limit >= visible.length;
      if (empty) empty.hidden = visible.length > 0 || cards.length === 0;
    }

    filterButtons.forEach((button) => {
      const isActive = button.dataset.filter === category;
      button.classList.toggle('active', isActive);
      button.setAttribute('aria-pressed', String(isActive));
      button.onclick = () => {
        category = button.dataset.filter;
        limit = perPage;
        filterButtons.forEach((item) => {
          const isSelected = item === button;
          item.classList.toggle('active', isSelected);
          item.setAttribute('aria-pressed', String(isSelected));
        });
        render();
      };
    });
    if (sort) sort.onchange = render;
    if (more) {
      more.onclick = () => {
        limit += perPage;
        render();
      };
    }
    if (controls) controls.hidden = false;
    render();
  }

  /* ---------- Collaboration video ---------- */
  function instagramEmbed(url) {
    try {
      const parsed = new URL(url);
      let path = parsed.pathname.replace(/\/embed\/?$/, '');
      if (!path.endsWith('/')) path += '/';
      return `https://www.instagram.com${path}embed/`;
    } catch (error) {
      return '';
    }
  }

  function initFilm(player) {
    const button = player.querySelector('[data-gf-play]');
    if (!button || button.dataset.initialized) return;
    button.dataset.initialized = 'true';
    button.onclick = () => {
      const template = player.querySelector('template[data-gf-video]');
      if (template) {
        const video = template.content.querySelector('video');
        if (video) {
          const clone = video.cloneNode(true);
          player.replaceChildren(clone);
          const playing = clone.play();
          if (playing) playing.catch(() => {});
          return;
        }
      }
      const src = instagramEmbed(button.dataset.instagram || '');
      if (!src) return;
      const frame = document.createElement('iframe');
      frame.src = src;
      frame.title = button.dataset.title || 'Video';
      frame.allow = 'autoplay; encrypted-media; fullscreen; picture-in-picture';
      frame.allowFullscreen = true;
      frame.referrerPolicy = 'strict-origin-when-cross-origin';
      player.replaceChildren(frame);
    };
  }

  /* ---------- Artwork carousel ---------- */
  function initCarousel(root) {
    const track = root.querySelector('[data-gf-track]');
    if (!track || track.dataset.initialized) return;
    track.dataset.initialized = 'true';

    const slides = [...track.children];
    const dots = [...root.querySelectorAll('.art-dot')];
    const position = root.querySelector('[data-gf-position]');
    let current = 0;
    let timer;
    let pointerInside = false;
    let focusInside = false;

    function stopAutoplay() {
      if (timer) window.clearInterval(timer);
      timer = undefined;
      carouselTimers.delete(root);
    }

    function updateAutoplay() {
      stopAutoplay();
      if (slides.length < 2 || reduced.matches || pointerInside || focusInside || document.hidden) return;
      const delay = Number(root.dataset.gfAutoRotate) || 7000;
      timer = window.setInterval(() => show(current + 1), delay);
      carouselTimers.set(root, timer);
    }

    function show(index) {
      current = (index + slides.length) % slides.length;
      track.scrollTo({ left: current * track.clientWidth, behavior: reduced.matches ? 'instant' : 'smooth' });
      updateAutoplay();
    }

    function sync() {
      if (!track.clientWidth) return;
      current = Math.max(0, Math.min(slides.length - 1, Math.round(track.scrollLeft / track.clientWidth)));
      dots.forEach((dot, i) => dot.setAttribute('aria-current', String(i === current)));
      if (position) position.textContent = `${current + 1} / ${slides.length}`;
    }

    const prev = root.querySelector('[data-gf-prev]');
    const next = root.querySelector('[data-gf-next]');
    if (prev) prev.onclick = () => show(current - 1);
    if (next) next.onclick = () => show(current + 1);
    dots.forEach((dot, i) => {
      dot.onclick = () => show(i);
    });
    track.onkeydown = (event) => {
      if (!['ArrowLeft', 'ArrowRight', 'Home', 'End'].includes(event.key)) return;
      event.preventDefault();
      if (event.key === 'Home') show(0);
      else if (event.key === 'End') show(slides.length - 1);
      else show(current + (event.key === 'ArrowRight' ? 1 : -1));
    };
    track.addEventListener('scroll', sync, { passive: true });
    const onPointerEnter = () => { pointerInside = true; updateAutoplay(); };
    const onPointerLeave = () => { pointerInside = false; updateAutoplay(); };
    const onFocusIn = () => { focusInside = true; updateAutoplay(); };
    const onFocusOut = (event) => {
      if (!root.contains(event.relatedTarget)) {
        focusInside = false;
        updateAutoplay();
      }
    };
    const onVisibilityChange = () => updateAutoplay();
    root.addEventListener('pointerenter', onPointerEnter);
    root.addEventListener('pointerleave', onPointerLeave);
    root.addEventListener('focusin', onFocusIn);
    root.addEventListener('focusout', onFocusOut);
    document.addEventListener('visibilitychange', onVisibilityChange);
    if (reduced.addEventListener) reduced.addEventListener('change', onVisibilityChange);
    carouselCleanups.set(root, () => {
      stopAutoplay();
      root.removeEventListener('pointerenter', onPointerEnter);
      root.removeEventListener('pointerleave', onPointerLeave);
      root.removeEventListener('focusin', onFocusIn);
      root.removeEventListener('focusout', onFocusOut);
      document.removeEventListener('visibilitychange', onVisibilityChange);
      if (reduced.removeEventListener) reduced.removeEventListener('change', onVisibilityChange);
    });
    updateAutoplay();

    if ('ResizeObserver' in window) {
      const observer = new ResizeObserver(() => track.scrollTo({ left: current * track.clientWidth, behavior: 'instant' }));
      observer.observe(track);
      observers.set(track, observer);
    }

    // In the theme editor, selecting an artwork block scrolls the carousel to it.
    slides.forEach((slide, i) => {
      slide.addEventListener('shopify:block:select', () => show(i));
    });
  }

  /* ---------- Header drop-down menus ---------- */
  function initMenus(scope) {
    scope.querySelectorAll('[data-gf-menu]').forEach((menu) => {
      if (menu.dataset.initialized) return;
      menu.dataset.initialized = 'true';
      menu.addEventListener('toggle', () => {
        if (!menu.open) return;
        document.querySelectorAll('[data-gf-menu][open]').forEach((other) => {
          if (other !== menu) other.open = false;
        });
      });
      menu.addEventListener('keydown', (event) => {
        if (event.key !== 'Escape' || !menu.open) return;
        menu.open = false;
        menu.querySelector('summary').focus();
      });
      menu.addEventListener('focusout', (event) => {
        if (menu.open && event.relatedTarget && !menu.contains(event.relatedTarget)) menu.open = false;
      });
    });
  }

  document.addEventListener('click', (event) => {
    document.querySelectorAll('[data-gf-menu][open]').forEach((menu) => {
      if (!menu.contains(event.target)) menu.open = false;
    });
  });

  /* ---------- Cart count ----------
     Dawn's add-to-cart, cart page and cart pop-up announce changes through its pubsub.js;
     the header count then reads the cart again. */
  async function refreshCartCount() {
    const counts = document.querySelectorAll('[data-gf-cart-count]');
    if (!counts.length) return;
    try {
      const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
      const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
      const cart = await response.json();
      counts.forEach((count) => {
        count.textContent = cart.item_count;
        count.hidden = cart.item_count === 0;
      });
    } catch (error) {
      /* The count updates on the next page load. */
    }
  }

  function watchCart() {
    if (typeof window.subscribe !== 'function' || typeof PUB_SUB_EVENTS === 'undefined') return;
    window.subscribe(PUB_SUB_EVENTS.cartUpdate, refreshCartCount);
  }

  /* ---------- Country / language selector ---------- */
  function initLocalization(scope) {
    scope.querySelectorAll('select[data-gf-localization]').forEach((select) => {
      select.onchange = () => select.form.submit();
    });
  }

  /* ---------- Start ---------- */
  function initialize(scope = document) {
    updateMotionButtons();
    initMenus(scope);
    initLocalization(scope);
    scope.querySelectorAll('[data-gf-shop]').forEach(initShop);
    scope.querySelectorAll('[data-gf-film]').forEach(initFilm);
    scope.querySelectorAll('[data-gf-carousel]').forEach(initCarousel);
  }

  setPaused(paused);
  if (reduced.addEventListener) reduced.addEventListener('change', (event) => setPaused(event.matches));
  initialize();
  watchCart();
  window.addEventListener('pageshow', (event) => {
    if (event.persisted) refreshCartCount();
  });

  document.addEventListener('shopify:section:load', (event) => {
    initialize(event.target);
    setPaused(paused);
  });
  document.addEventListener('shopify:section:unload', (event) => {
    const track = event.target.querySelector('[data-gf-track]');
    if (track && observers.get(track)) observers.get(track).disconnect();
    const carousel = event.target.querySelector('[data-gf-carousel]');
    const cleanup = carousel && carouselCleanups.get(carousel);
    if (cleanup) cleanup();
  });
})();
